import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Stack,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Close,
  DeleteOutline,
  Add,
  Remove,
  ShoppingBagOutlined,
  HttpsOutlined,
} from "@mui/icons-material";
import { useCart } from "../../context/CartContext";
import { useNavigate } from "react-router-dom";
const BRAND_PRIMARY = "#2E4053";
const BRAND_BG = "#D1D8E0";

const CartModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Inside CartModal component
  const navigate = useNavigate();
  const handleCheckoutClick = () => {
    onClose(); // Close the modal
    navigate("/shop/checkout");
  };
  const {
    cartItems,
    removeFromCart,
    updateQuantity,
    subtotal,
    taxAmount,
    total,
    currencySymbol,
    totalItems,
  } = useCart();

  return (
    <Drawer
      // Switches to bottom sheet on mobile for better thumb reach
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      variant="temporary"
      SlotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(BRAND_PRIMARY, 0.4),
            backdropFilter: "blur(4px)",
          },
        },
      }}
      PaperProps={{
        sx: {
          /* Responsive Geometry */
          width: { xs: "100%", sm: 400 },
          height: { xs: "auto", sm: "calc(100% - 40px)" },
          maxHeight: { xs: "90vh", sm: "calc(100% - 40px)" },
          margin: { xs: 0, sm: "20px" },
          borderRadius: { xs: "20px 20px 0 0", sm: "16px" },

          display: "flex",
          flexDirection: "column",
          backgroundColor: "#FFFFFF",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          overflow: "hidden",
        },
      }}
    >
      {/* Mobile Handle - Visual indicator for bottom sheets */}
      {isMobile && (
        <Box
          sx={{
            width: 40,
            height: 4,
            bgcolor: "#E0E0E0",
            borderRadius: 2,
            mx: "auto",
            mt: 1.5,
          }}
        />
      )}

      {/* Header */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: BRAND_PRIMARY,
          color: "white",
          mt: isMobile ? 1 : 0,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <ShoppingBagOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight="700"
                sx={{ lineHeight: 1.2 }}
              >
                Order Summary
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {totalItems} Items
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Cart Items Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2, sm: 3 },
          py: 2,
          bgcolor: "#FAFAFB",
        }}
      >
        {cartItems.length === 0 ? (
          <Stack alignItems="center" py={10} sx={{ opacity: 0.5 }}>
            <ShoppingBagOutlined sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2">Your cart is empty</Typography>
          </Stack>
        ) : (
          cartItems.map((item) => (
            <Box
              key={item.id}
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: "white",
                borderRadius: "12px",
                border: "1px solid #F0F0F0",
              }}
            >
              <Stack direction="row" spacing={2}>
                <Box
                  component="img"
                  src={item.image}
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "8px",
                    objectFit: "cover",
                    bgcolor: BRAND_BG,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      fontSize="0.85rem"
                      fontWeight="700"
                      color={BRAND_PRIMARY}
                      noWrap
                      sx={{ maxWidth: "150px" }}
                    >
                      {item.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeFromCart(item.id)}
                      sx={{ mt: -1 }}
                    >
                      <DeleteOutline fontSize="small" color="error" />
                    </IconButton>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 1 }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{ border: "1px solid #E0E0E0", borderRadius: "6px" }}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            Math.max(1, item.quantity - 1)
                          )
                        }
                      >
                        <Remove sx={{ fontSize: 12 }} />
                      </IconButton>
                      <Typography
                        variant="caption"
                        fontWeight="700"
                        sx={{ px: 1 }}
                      >
                        {item.quantity}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          updateQuantity(item.id, item.quantity + 1)
                        }
                      >
                        <Add sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Stack>
                    <Typography variant="body2" fontWeight="700">
                      {currencySymbol}
                      {(item.price * item.quantity).toLocaleString()}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          ))
        )}
      </Box>

      {/* Footer */}
      {cartItems.length > 0 && (
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            bgcolor: "white",
            borderTop: `1px solid ${BRAND_BG}`,
            pb: isMobile ? 4 : 3,
          }}
        >
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                Subtotal
              </Typography>
              <Typography variant="caption" fontWeight="600">
                {currencySymbol}
                {subtotal.toFixed(2)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6" fontWeight="700">
                Total
              </Typography>
              <Typography variant="h6" fontWeight="800" color={BRAND_PRIMARY}>
                {currencySymbol}
                {total.toFixed(2)}
              </Typography>
            </Stack>
          </Stack>

          <Button
            variant="contained"
            fullWidth
            onClick={handleCheckoutClick}
            sx={{
              bgcolor: BRAND_PRIMARY,
              py: 1.5,
              fontWeight: "700",
              textTransform: "none",
              borderRadius: "10px",
            }}
          >
            Checkout Now
          </Button>

          <Stack
            direction="row"
            spacing={0.5}
            justifyContent="center"
            sx={{ mt: 1.5, opacity: 0.6 }}
          >
            <HttpsOutlined sx={{ fontSize: 12 }} />
            <Typography sx={{ fontSize: "10px" }} fontWeight="600">
              Secure Enterprise Checkout
            </Typography>
          </Stack>
        </Box>
      )}
    </Drawer>
  );
};

export default CartModal;
